'use client';

import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { MultiSelect } from '@/shared/ui/multi-select';
import { FormFieldMessage } from './form-field-message';
import { FormFieldOptions, FormFieldProps } from './types';
import { getFormFieldState } from './utils/getFormFieldState';
import { mergeRefs } from './utils/mergeRefs';

type Props = FormFieldProps<typeof MultiSelect.Trigger> & {
    triggerContent: ReactNode;
    options: FormFieldOptions;
};

export const FormMultiSelect = ({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    messageWrapperProps,
    isErrorVisible = true,
    isSuccessVisible,
    triggerContent,
    options,
    ref,
    ...props
}: Props) => (
    <Controller
        {...{ name, control, defaultValue, disabled, rules, shouldUnregister }}
        render={({
            field: { ref: fieldRef, onChange, value: selectedValue },
            fieldState: { error, isDirty, isTouched, invalid },
        }) => {
            const state = getFormFieldState({
                invalid,
                isDirty,
                isTouched,
                isErrorVisible,
                isSuccessVisible,
            });

            return (
                <>
                    <MultiSelect
                        values={selectedValue}
                        defaultValues={selectedValue}
                        onValuesChange={onChange}
                    >
                        <MultiSelect.Trigger state={state} ref={mergeRefs([ref, fieldRef])} {...props}>
                            {triggerContent}
                        </MultiSelect.Trigger>
                        <MultiSelect.Content state={state}>
                            {typeof options === 'function'
                                ? options(onChange)
                                : options.map(({ label, value }) => (
                                      <MultiSelect.Item key={value} value={value}>
                                          {label}
                                      </MultiSelect.Item>
                                  ))}
                        </MultiSelect.Content>
                    </MultiSelect>
                    <FormFieldMessage state={state} error={error} messageWrapperProps={messageWrapperProps} />
                </>
            );
        }}
    />
);
